import React, { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';

import useTranslate from '@src/i18n/useTranslate';
import { Order } from '@src/core/buyerApiClient';
import { getConvertedOrderStatusType, orderSaleItems } from '@src/common/order';
import { loadOrderVouchers, orderVoucherInfoSelector } from '@src/features/order';
import { Container, Wrapper } from '../shared';

import Reservation from './Reservation';
import HeaderContent from './HeaderContent';
import { track } from '@src/services/analytics';

import { groupByPackageUnitId } from '@src/common/cart';

import {
  Button,
  FooterInfo,
  GoBackButton,
  HeaderTitle,
  PackageContainer,
  PackageHeader,
  PackageItemList,
  PackageTitle,
  ReservationWrapper,
} from './styles';

type Props = {
  order: Order;
  onClose?: () => void;
  onOrderCheckout?: (order: Order) => void;
};

const SaleDisplay = (props: Props) => {
  const { order, onClose, onOrderCheckout = () => {} } = props;
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { type } = getConvertedOrderStatusType(order.status);
  const { hasMultipleVouchers } = useAppSelector(orderVoucherInfoSelector(order.id));

  useEffect(() => {
    track('saleAccess', { orderId: order.id });
  }, []);

  useEffect(() => {
    if (order) {
      dispatch(loadOrderVouchers(order));
    }
  }, []);

  const saleItems = useMemo(() => orderSaleItems(order), [order]);
  const { individualItems, packageGroup } = useMemo(() => groupByPackageUnitId(saleItems), [saleItems]);

  return (
    <Wrapper>
      <Container style={{ maxWidth: 876 }}>
        <HeaderTitle>{i18n('account_myPurchases')}</HeaderTitle>
        <HeaderContent order={order} />

        {packageGroup.map((packageGroupItem) => (
          <PackageContainer key={packageGroupItem.productPackage.id}>
            <PackageHeader>
              <PackageTitle>
                {i18n('account_sale_package_title', {
                  quantity: String(packageGroupItem.items.length),
                  packageName: packageGroupItem.productPackage.name,
                })}
              </PackageTitle>
            </PackageHeader>

            <PackageItemList>
              {packageGroupItem.items.map((item) => (
                <Reservation
                  isPackageItem
                  disableReschedule
                  order={order}
                  saleItem={item}
                  key={item.id}
                  hasMultipleVouchers={hasMultipleVouchers}
                />
              ))}
            </PackageItemList>
          </PackageContainer>
        ))}

        {individualItems.length > 0 ? (
          <ReservationWrapper>
            {individualItems.map((item) => (
              <Reservation order={order} saleItem={item} key={item.id} hasMultipleVouchers={hasMultipleVouchers} />
            ))}
          </ReservationWrapper>
        ) : null}

        <FooterInfo>
          {type === 'danger' || type === 'warning' ? <p>{i18n('sale_noAvailableReservation')}</p> : null}
          {type === 'info' ? (
            <Button onClick={() => onOrderCheckout(order)}>{i18n('account_sale_checkout')}</Button>
          ) : null}
          {onClose ? <GoBackButton onClick={onClose}>{i18n('sale_goBack')}</GoBackButton> : null}
        </FooterInfo>
      </Container>
    </Wrapper>
  );
};

export default SaleDisplay;
